import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { useData } from "../hooks/useData";
import { I } from "./Icons";
import { clsx } from "./Primitives";
import FormDialog from "./FormDialog";
import type { Partner } from "../types";

export interface Lead {
  id: string; email: string; name: string | null; company: string | null; title: string | null; phone: string | null;
  country: string | null; market: string | null; language: string | null; interests: string[]; source: string; sources: string[];
  source_detail: string | null; page_url: string | null; utm: Record<string, string> | null; metadata: Record<string, unknown> | null;
  consent: boolean; consent_text: string | null; consent_at: string | null; subscribed: boolean; unsubscribed_at: string | null;
  status: LeadStatus; owner: string | null; owner_name?: string | null; notes: string | null; contact_id: string | null;
  signup_count: number; first_seen_at: string; last_seen_at: string;
}
type LeadStatus = "new" | "contacted" | "qualified" | "converted" | "not_relevant";
interface LeadEvent { id: number; type: string; source: string | null; detail: Record<string, unknown> | null; created_at: string }

const STATUS: { id: LeadStatus; label: string }[] = [
  { id: "new", label: "New" },
  { id: "contacted", label: "Contacted" },
  { id: "qualified", label: "Qualified" },
  { id: "converted", label: "Converted" },
  { id: "not_relevant", label: "Not relevant" },
];
const statusLabel = (s: string) => STATUS.find(x => x.id === s)?.label || s;

function ago(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 60) return m <= 1 ? "just now" : `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
const when = (iso: string) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Leads({ go, partners, bump }: {
  go: (screen: string, id?: string | null) => void;
  partners: Record<string, Partner>;
  bump: () => void;
}) {
  const [status, setStatus] = useState<string>("");
  const [source, setSource] = useState("");
  const [subscribed, setSubscribed] = useState("");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [selId, setSelId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 250); return () => clearTimeout(t); }, [q]);

  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (source) params.set("source", source);
  if (subscribed) params.set("subscribed", subscribed);
  if (query) params.set("q", query);
  const qs = params.toString();
  const { data, refetch } = useData<{ leads: Lead[]; sources: { source: string; n: number }[]; counts: Record<string, number> }>(
    `/api/leads${qs ? "?" + qs : ""}`);
  const leads = data?.leads || [];
  const total = Object.values(data?.counts || {}).reduce((a, b) => a + b, 0);
  const sel = leads.find(l => l.id === selId) || leads[0] || null;

  const changed = () => { refetch(); bump(); };

  return (
    <div className="screen leads-screen">
      <div className="page-head">
        <div>
          <h1 className="page-title">Leads</h1>
          <div className="page-sub">Newsletter and website sign-ups from info.saga-advisory.com and www.saga-advisory.com</div>
        </div>
        <div className="cd-actions">
          <a className="btn ghost" href={`/api/leads/export.csv${qs ? "?" + qs : ""}`} style={{ textDecoration: "none" }}>
            {I.doc({ size: 14 })}Export CSV
          </a>
          <button className="btn primary" onClick={() => setAdding(true)}>{I.plus({ size: 15 })}Add lead</button>
        </div>
      </div>

      <div className="ld-filters">
        <div className="seg">
          <button className={clsx("seg-btn", !status && "active")} onClick={() => setStatus("")}>All <span className="ld-n">{total}</span></button>
          {STATUS.map(s => (
            <button key={s.id} className={clsx("seg-btn", status === s.id && "active")} onClick={() => setStatus(s.id)}>
              {s.label} <span className="ld-n">{data?.counts?.[s.id] || 0}</span>
            </button>
          ))}
        </div>
        <select className="ld-select" value={source} onChange={e => setSource(e.target.value)} aria-label="Source">
          <option value="">All sources</option>
          {(data?.sources || []).map(s => <option key={s.source} value={s.source}>{s.source} ({s.n})</option>)}
        </select>
        <select className="ld-select" value={subscribed} onChange={e => setSubscribed(e.target.value)} aria-label="Newsletter">
          <option value="">Subscribed or not</option>
          <option value="1">Subscribed</option>
          <option value="0">Unsubscribed</option>
        </select>
        <input className="set-search ld-search" placeholder="Search name, email, company, interest…" value={q} onChange={e => setQ(e.target.value)} />
      </div>

      {data && leads.length === 0 ? (
        <div className="card ld-empty">
          {total === 0 ? (
            <>
              <h3>No leads yet</h3>
              <p>Sign-ups arrive here automatically once the websites send them to the CRM.
                An admin creates a key for each site under <strong>Settings → API keys</strong>.</p>
            </>
          ) : <p>No leads match these filters.</p>}
        </div>
      ) : (
        <div className="ld-layout">
          <div className="ld-list">
            {leads.map(l => (
              <button key={l.id} className={clsx("ld-row", sel?.id === l.id && "sel")} onClick={() => setSelId(l.id)}>
                <span className="ld-av">{(l.name || l.email).slice(0, 1).toUpperCase()}</span>
                <span className="ld-row-main">
                  <span className="ld-row-top">
                    <span className="ld-name">{l.name || l.email}</span>
                    <span className="ld-when">{ago(l.last_seen_at)}</span>
                  </span>
                  <span className="ld-row-sub">{[l.company, l.country].filter(Boolean).join(" · ") || l.email}</span>
                  <span className="ld-row-tags">
                    <span className={clsx("ld-status", "st-" + l.status)}>{statusLabel(l.status)}</span>
                    {!l.subscribed && <span className="ld-tag muted">Unsubscribed</span>}
                    {l.sources.map(s => <span key={s} className="ld-tag src">{s.replace(/^www\./, "")}</span>)}
                  </span>
                </span>
              </button>
            ))}
          </div>
          {sel && <LeadDetail key={sel.id} id={sel.id} partners={partners} go={go} onChanged={changed}
            onDeleted={() => { setSelId(null); changed(); }} />}
        </div>
      )}

      {adding && (
        <FormDialog
          title="Add lead"
          sub="Someone you met — they're added as a lead and a contact."
          initial={{ email: "", name: "", company: "", title: "", country: "", interests: "" }}
          fields={[
            { key: "email", label: "Email", required: true },
            { key: "name", label: "Name" },
            { key: "company", label: "Company" },
            { key: "title", label: "Title" },
            { key: "country", label: "Country" },
            { key: "interests", label: "Interests (comma separated)", wide: true },
          ]}
          saveLabel="Add lead"
          onClose={() => setAdding(false)}
          onSave={async v => {
            const lead = await api.post<Lead>("/api/leads", v);
            setAdding(false); setSelId(lead.id); changed();
          }}
        />
      )}
    </div>
  );
}

const EVENT_LABEL: Record<string, string> = {
  signup: "Signed up", resignup: "Signed up again", update: "Details updated", unsubscribe: "Unsubscribed",
  status: "Status changed", owner: "Owner changed", note: "Notes edited",
};

function LeadDetail({ id, partners, go, onChanged, onDeleted }: {
  id: string; partners: Record<string, Partner>; go: (screen: string, id?: string | null) => void;
  onChanged: () => void; onDeleted: () => void;
}) {
  const { data: l, refetch } = useData<Lead & { events: LeadEvent[]; contact_name?: string; contact_archived?: string | null }>(`/api/leads/${id}`);
  const [notes, setNotes] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const notesDirty = l && notes !== null && notes !== (l.notes || "");

  const update = async (patch: Record<string, unknown>) => {
    setErr(null);
    try { await api.put(`/api/leads/${id}`, patch); refetch(); onChanged(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const remove = async (erase: boolean) => {
    if (!l) return;
    const msg = erase
      ? `Erase ${l.email} completely?\n\nThe lead, its history and the contact created for it are deleted — use this when someone asks to be forgotten. It can't be undone.`
      : `Delete the lead ${l.email}? The contact stays in Accounts.`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/api/leads/${id}${erase ? "?erase=1" : ""}`); onDeleted(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const utm = useMemo(() => Object.entries(l?.utm || {}).map(([k, v]) => `${k.replace(/^utm_/, "")}: ${v}`).join(" · "), [l?.utm]);
  if (!l) return <div className="ld-detail" />;

  return (
    <div className="ld-detail">
      <div className="ld-d-head">
        <div>
          <h2 className="ad-name">{l.name || l.email}</h2>
          <div className="ld-d-sub">{[l.title, l.company].filter(Boolean).join(", ") || "No company given"}</div>
        </div>
        <div className="cd-actions">
          <a className="btn ghost" href={`mailto:${l.email}`} style={{ textDecoration: "none" }}>{I.send({ size: 14 })}Email</a>
          <button className="btn ghost" onClick={() => setEditing(true)}>{I.sliders({ size: 14 })}Edit</button>
        </div>
      </div>

      <div className="ld-controls">
        <label className="set-field">
          <span className="set-label">Status</span>
          <select value={l.status} onChange={e => update({ status: e.target.value })}>
            {STATUS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </label>
        <label className="set-field">
          <span className="set-label">Owner</span>
          <select value={l.owner || ""} onChange={e => update({ owner: e.target.value || null })}>
            <option value="">Unassigned</option>
            {Object.values(partners).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      </div>
      {err && <div className="set-notice err" style={{ margin: "0 0 12px" }}>{err}</div>}

      <dl className="ld-facts">
        <dt>Email</dt><dd><a href={`mailto:${l.email}`}>{l.email}</a></dd>
        {l.phone && <><dt>Phone</dt><dd>{l.phone}</dd></>}
        {(l.country || l.market) && <><dt>Country</dt><dd>{l.country}{l.market && l.market.toLowerCase() !== (l.country || "").toLowerCase() ? ` (${l.market})` : ""}</dd></>}
        {l.language && <><dt>Language</dt><dd>{l.language}</dd></>}
        <dt>Newsletter</dt>
        <dd>
          {l.subscribed ? <span className="ld-ok">Subscribed</span> : <span className="ld-muted">Unsubscribed {l.unsubscribed_at && ago(l.unsubscribed_at)}</span>}
          {" · "}
          {l.consent ? <span title={l.consent_text || ""}>consent given{l.consent_at ? ` ${when(l.consent_at)}` : ""}</span> : <span className="ld-warn">no consent recorded</span>}
          <button className="ld-link" onClick={() => update({ subscribed: !l.subscribed })}>{l.subscribed ? "Mark unsubscribed" : "Mark subscribed"}</button>
        </dd>
        {l.consent_text && <><dt>Consent wording</dt><dd className="ld-quote">“{l.consent_text}”</dd></>}
        {l.interests.length > 0 && <><dt>Interests</dt><dd className="ld-chips">{l.interests.map(i => <span key={i} className="set-tag">{i}</span>)}</dd></>}
        <dt>Came from</dt>
        <dd>
          {l.sources.join(", ")}
          {l.source_detail && <> · {l.source_detail}</>}
          {l.page_url && <> · <a href={l.page_url} target="_blank" rel="noreferrer">page</a></>}
          {l.signup_count > 1 && <> · signed up {l.signup_count} times</>}
        </dd>
        {utm && <><dt>Campaign</dt><dd>{utm}</dd></>}
        <dt>First seen</dt><dd>{when(l.first_seen_at)}</dd>
        <dt>Contact</dt>
        <dd>
          {l.contact_id && !l.contact_archived && l.company
            ? <button className="ld-link" style={{ marginLeft: 0 }} onClick={() => go("clients", l.contact_id)}>Open {l.contact_name || "contact"} in Accounts →</button>
            : <span className="ld-muted">{l.contact_id ? "Saved as a contact (shown under Accounts once a company is added)" : "No contact"}</span>}
        </dd>
      </dl>

      <label className="set-field ld-notes">
        <span className="set-label">Notes</span>
        <textarea rows={3} value={notes ?? l.notes ?? ""} placeholder="Who they are, what they want, next step…"
          onChange={e => setNotes(e.target.value)} />
      </label>
      {notesDirty && (
        <div className="ld-notes-foot">
          <button className="btn ghost sm" onClick={() => setNotes(null)}>Cancel</button>
          <button className="btn primary sm" onClick={async () => { await update({ notes }); setNotes(null); }}>Save notes</button>
        </div>
      )}

      <div className="section-label ld-h">History</div>
      <div className="ld-events">
        {l.events.map(e => (
          <div key={e.id} className="ld-event">
            <span className="ld-event-dot" />
            <span className="ld-event-what">
              {EVENT_LABEL[e.type] || e.type}
              {e.type === "status" && e.detail ? ` → ${statusLabel(String(e.detail.to))}` : ""}
              {e.type === "owner" && e.detail ? ` → ${e.detail.to ? partners[String(e.detail.to)]?.name || e.detail.to : "unassigned"}` : ""}
              {e.source && <span className="ld-muted"> · {partners[e.source]?.name || e.source}</span>}
              {(e.type === "signup" || e.type === "resignup") && e.detail?.form ? <span className="ld-muted"> · {String(e.detail.form)}</span> : null}
            </span>
            <span className="ld-when">{when(e.created_at)}</span>
          </div>
        ))}
      </div>

      <div className="ld-danger">
        <button className="btn ghost sm set-danger" onClick={() => remove(false)}>{I.trash({ size: 13 })}Delete lead</button>
        <button className="btn ghost sm set-danger" onClick={() => remove(true)}>Erase person (GDPR)</button>
      </div>

      {editing && (
        <FormDialog
          title="Edit lead"
          sub={l.email}
          initial={{ name: l.name || "", company: l.company || "", title: l.title || "", phone: l.phone || "", country: l.country || "" }}
          fields={[
            { key: "name", label: "Name" }, { key: "company", label: "Company" }, { key: "title", label: "Title" },
            { key: "phone", label: "Phone" }, { key: "country", label: "Country" },
          ]}
          onClose={() => setEditing(false)}
          onSave={async v => { await api.put(`/api/leads/${id}`, v); setEditing(false); refetch(); onChanged(); }}
        />
      )}
    </div>
  );
}
